import { useMemo, useRef, useState, type RefObject } from 'react';
import { Button, StarIcon, Tooltip, TOOLTIP_SURFACE } from '@repo/atomic-components';
import type { ToolkitTool } from '@/api/toolkit';
import { useToolkitGridCollapsedLimit } from '../hooks';
import { CREATE_USER_TOOLKIT_PROMPT_LABEL } from './CreateUserToolkitPromptModal';
import { MyPromptToolItem } from './MyPromptToolItem';

interface MyPromptsSectionProps {
  prompts: ToolkitTool[];
  compact?: boolean;
  handleToolClick: (tool: ToolkitTool) => void;
  onCreatePrompt: () => void;
  onEditPrompt: (tool: ToolkitTool) => void;
  onRequestGlobalToolkit: (tool: ToolkitTool) => void;
  onDeletePrompt: (tool: ToolkitTool) => void;
  onExpandPrompt: (tool: ToolkitTool) => void;
}

export const MyPromptsSection = ({
  prompts,
  compact = false,
  handleToolClick,
  onCreatePrompt,
  onEditPrompt,
  onRequestGlobalToolkit,
  onDeletePrompt,
  onExpandPrompt,
}: MyPromptsSectionProps) => {
  const [expanded, setExpanded] = useState(false);
  const { containerRef, collapsedLimit } = useToolkitGridCollapsedLimit(compact);
  const createPromptTooltipRef = useRef<HTMLSpanElement>(null);

  const gridClassName = compact ? 'toolkit-grid--compact gap-6' : 'toolkit-grid gap-6';

  const needsToggle = prompts.length > collapsedLimit;

  const visiblePrompts = useMemo(() => {
    if (expanded || !needsToggle) {
      return prompts;
    }
    return prompts.slice(0, collapsedLimit);
  }, [collapsedLimit, expanded, needsToggle, prompts]);

  return (
    <div ref={containerRef} className="flex flex-col gap-6">
      <div className="flex items-center gap-2">
        <StarIcon className="text-theme-dark-green h-[20px] w-[20px] shrink-0 [&>path]:fill-current" aria-hidden />
        <h3 className="text-theme-black text-lg font-semibold">My prompts</h3>
        <span ref={createPromptTooltipRef} className="inline-flex shrink-0">
          <Button
            type="button"
            outlined
            className="text-theme-black border-theme-border-grey hover:text-theme-dark-green hover:bg-theme-background-grey flex h-8 w-8 shrink-0 items-center justify-center text-lg leading-none"
            aria-label={CREATE_USER_TOOLKIT_PROMPT_LABEL}
            onClick={onCreatePrompt}
          >
            <span aria-hidden>+</span>
          </Button>
          <Tooltip
            target={createPromptTooltipRef as RefObject<HTMLElement>}
            content={CREATE_USER_TOOLKIT_PROMPT_LABEL}
            position="top"
            className={TOOLTIP_SURFACE}
          />
        </span>
      </div>

      {prompts.length === 0 ? (
        <p className="text-theme-text-grey text-sm">Prompts you create or save will appear here.</p>
      ) : (
        <div className={gridClassName}>
          {visiblePrompts.map((tool) => (
            <MyPromptToolItem
              key={tool.toolId}
              tool={tool}
              handleToolClick={handleToolClick}
              onEdit={onEditPrompt}
              onRequestGlobalToolkit={onRequestGlobalToolkit}
              onDelete={onDeletePrompt}
              onExpand={onExpandPrompt}
            />
          ))}
        </div>
      )}

      {needsToggle && (
        <div className="flex justify-center">
          <Button
            type="button"
            outlined
            className="text-theme-dark-green border-0 text-sm hover:bg-transparent"
            onClick={() => setExpanded((v) => !v)}
          >
            {expanded ? 'View less' : 'View more'}
          </Button>
        </div>
      )}
    </div>
  );
};
